import React, { useState, useEffect, useCallback, useRef } from 'react';
import TopSearchBar from './JobSearch/TopSearchBar';
import JobListingList from './JobSearch/JobListingList';
import JobDetailPanel from './JobSearch/JobDetailPanel';
import MobileFilterModal from './JobSearch/MobileFilterModal';
import MobileSearchModal from './JobSearch/MobileSearchModal';
import SaveSearchConditionModal from './SaveSearchConditionModal';
import { saveSearchConditions, loadSearchConditions, SearchFilters as StoredFilters } from '../utils/searchStorage';
import { trackJobSearch } from '../utils/tracking';
import * as styles from './JobSearchPage.module.scss';

interface JobSearchPageProps {
  initialKeyword?: string;
  initialFilters?: SearchFilters;
  initialData?: {
    job_listings: any[];
    pagination: any;
    total_job_listings_count?: number;
  };
  totalJobListingsCount?: number;
  currentUserId?: number;
  isUserSignedIn?: boolean;
  isProfileComplete?: boolean;
  isServerRendered?: boolean; // SSRかどうかのフラグ
}

interface SearchFilters {
  keyword?: string;
  skill_ids?: number[];
  occupation_ids?: number[];
  industry_ids?: number[];
  characteristic_ids?: number[];
  prefecture_ids?: number[];
  workstyle_types?: string[];
  availability_types?: string[];
  price_over?: number;
  payment_site?: string;
  settlement_hours?: string;
  interview_count_max?: number;
  published_within?: number;
  is_recruiting?: boolean;
  remote_types?: string[];
  workday_types?: string[];
  job_source_ids?: number[];
  sort?: string;
  page?: number;
}

const JobSearchPage: React.FC<JobSearchPageProps> = ({
  initialKeyword,
  initialFilters,
  initialData,
  totalJobListingsCount,
  currentUserId,
  isUserSignedIn = false,
  isProfileComplete = false,
  isServerRendered = false
}) => {
  const [filters, setFilters] = useState<SearchFilters>(() => {
    
    const defaultFilters = { 
      keyword: initialKeyword || '', 
      page: 1, 
      sort: 'created_at desc', 
      is_recruiting: false, 
      prefecture_ids: [],
      skill_ids: [],
      occupation_ids: [],
      industry_ids: [],
      characteristic_ids: [],
      workstyle_types: [],
      availability_types: [],
      remote_types: [],
      workday_types: [],
      job_source_ids: []
    };
    
    if (initialFilters) {
      // Clean initialFilters to remove null values that would override defaults
      const cleanedFilters = Object.fromEntries(
        Object.entries(initialFilters).filter(([_, v]) => v != null)
      );
      
      const result = {
        ...defaultFilters,
        ...cleanedFilters,
        // 配列フィールドは確実に配列として設定（nilや空の場合はデフォルトの空配列を使用）
        skill_ids: initialFilters.skill_ids && initialFilters.skill_ids.length > 0 ? initialFilters.skill_ids : [],
        occupation_ids: initialFilters.occupation_ids && initialFilters.occupation_ids.length > 0 ? initialFilters.occupation_ids : [],
        industry_ids: initialFilters.industry_ids && initialFilters.industry_ids.length > 0 ? initialFilters.industry_ids : [],
        characteristic_ids: initialFilters.characteristic_ids && initialFilters.characteristic_ids.length > 0 ? initialFilters.characteristic_ids : [],
        prefecture_ids: initialFilters.prefecture_ids && initialFilters.prefecture_ids.length > 0 ? initialFilters.prefecture_ids : [],
        workstyle_types: initialFilters.workstyle_types && initialFilters.workstyle_types.length > 0 ? initialFilters.workstyle_types : [],
        availability_types: initialFilters.availability_types && initialFilters.availability_types.length > 0 ? initialFilters.availability_types : [],
        remote_types: initialFilters.remote_types && initialFilters.remote_types.length > 0 ? initialFilters.remote_types : [],
        workday_types: initialFilters.workday_types && initialFilters.workday_types.length > 0 ? initialFilters.workday_types : [],
        job_source_ids: initialFilters.job_source_ids && initialFilters.job_source_ids.length > 0 ? initialFilters.job_source_ids : []
      };
      return result;
    }
    
    return defaultFilters;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [jobListings, setJobListings] = useState(initialData?.job_listings || []);
  const [pagination, setPagination] = useState(initialData?.pagination || null);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [selectedJobDetail, setSelectedJobDetail] = useState<any>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSearchModal, setShowMobileSearchModal] = useState(false);
  const [showSaveSearchConditionModal, setShowSaveSearchConditionModal] = useState(false);
  // SSR時は初期値をfalseにし、クライアント側でのマウント後にtrueにする
  // これによりSSRとクライアント側で同じ初期レンダリングを保証する
  const [isHydrated, setIsHydrated] = useState(false);
  const hasInitialData = useRef(!!initialData?.job_listings?.length);
  const [savedSearchConditionId, setSavedSearchConditionId] = useState<number | null>(null);
  const [isCheckingSavedCondition, setIsCheckingSavedCondition] = useState(false);
  
  // スキル、職種、業界、特徴、都道府県の名前を格納するstate
  const [skillNames, setSkillNames] = useState<{[key: number]: string}>({});
  const [occupationNames, setOccupationNames] = useState<{[key: number]: string}>({});
  const [industryNames, setIndustryNames] = useState<{[key: number]: string}>({});
  const [characteristicNames, setCharacteristicNames] = useState<{[key: number]: string}>({});
  const [prefectureNames, setPrefectureNames] = useState<{[key: number]: string}>({});
  const [jobSourceNames, setJobSourceNames] = useState<{[key: number]: string}>({});

  // クライアント側でのマウント検知
  useEffect(() => {
    setIsHydrated(true);
    
    // SSRでない場合のみURLから初期状態を復元
    if (!isServerRendered) {
      const params = new URLSearchParams(window.location.search);
      const filtersFromUrl = parseUrlParams(params);
      setFilters(filtersFromUrl);
    }
  }, [isServerRendered]);

  // スキル、職種、業界、特徴、都道府県の名前を取得するuseEffect
  useEffect(() => {
    const fetchNames = async () => {
      try {
        // スキルの名前を取得
        if (filters.skill_ids && filters.skill_ids.length > 0) {
          const skillsResponse = await fetch('/api/v1/skills');
          const skillsData = await skillsResponse.json();
          const skillNameMap: {[key: number]: string} = {};
          
          // スキルAPIは階層構造なので平坦化する
          const allSkills: any[] = [];
          skillsData.forEach((category: any) => {
            if (category.skills) {
              category.skills.forEach((skill: any) => {
                allSkills.push(skill);
              });
            }
          });
          
          allSkills.forEach((skill: any) => {
            if (filters.skill_ids?.includes(skill.id)) {
              skillNameMap[skill.id] = skill.name;
            }
          });
          setSkillNames(skillNameMap);
        }

        // 職種の名前を取得
        if (filters.occupation_ids && filters.occupation_ids.length > 0) {
          const occupationsResponse = await fetch('/api/v1/occupations');
          const occupationsData = await occupationsResponse.json();
          const occupationNameMap: {[key: number]: string} = {};
          
          // 職種APIも階層構造なので平坦化する
          const allOccupations: any[] = [];
          occupationsData.forEach((occupation: any) => {
            allOccupations.push(occupation);
            if (occupation.children && occupation.children.length > 0) {
              occupation.children.forEach((child: any) => {
                allOccupations.push(child);
              });
            }
          });
          
          allOccupations.forEach((occupation: any) => {
            if (filters.occupation_ids?.includes(occupation.id)) {
              occupationNameMap[occupation.id] = occupation.name;
            }
          });
          setOccupationNames(occupationNameMap);
        }

        // 業界の名前を取得
        if (filters.industry_ids && filters.industry_ids.length > 0) {
          const industriesResponse = await fetch('/api/v1/industries');
          const industriesData = await industriesResponse.json();
          const industryNameMap: {[key: number]: string} = {};
          industriesData.forEach((industry: any) => {
            if (filters.industry_ids?.includes(industry.id)) {
              industryNameMap[industry.id] = industry.name;
            }
          });
          setIndustryNames(industryNameMap);
        }

        // 特徴の名前を取得
        if (filters.characteristic_ids && filters.characteristic_ids.length > 0) {
          const characteristicsResponse = await fetch('/api/v1/characteristics');
          const characteristicsData = await characteristicsResponse.json();
          const characteristicNameMap: {[key: number]: string} = {};
          characteristicsData.forEach((characteristic: any) => {
            if (filters.characteristic_ids?.includes(characteristic.id)) {
              characteristicNameMap[characteristic.id] = characteristic.name;
            }
          });
          setCharacteristicNames(characteristicNameMap);
        }

        // 都道府県の名前を取得
        if (filters.prefecture_ids && filters.prefecture_ids.length > 0) {
          const prefecturesResponse = await fetch('/api/v1/prefectures');
          const prefecturesData = await prefecturesResponse.json();
          const prefectureNameMap: {[key: number]: string} = {};
          prefecturesData.forEach((prefecture: any) => {
            if (filters.prefecture_ids?.includes(prefecture.id)) {
              prefectureNameMap[prefecture.id] = prefecture.name;
            }
          });
          setPrefectureNames(prefectureNameMap);
        }

        // フリーランスエージェントの名前を取得
        if (filters.job_source_ids && filters.job_source_ids.length > 0) {
          const jobSourcesResponse = await fetch('/api/v1/job_sources/for_search_filters');
          const jobSourcesData = await jobSourcesResponse.json();
          const jobSourceNameMap: {[key: number]: string} = {};
          jobSourcesData.forEach((jobSource: any) => {
            if (filters.job_source_ids?.includes(jobSource.id)) {
              jobSourceNameMap[jobSource.id] = jobSource.display_name || jobSource.name;
            }
          });
          setJobSourceNames(jobSourceNameMap);
        }
      } catch (error) {
        console.error('Failed to fetch names:', error);
      }
    };

    fetchNames();
  }, [filters.skill_ids, filters.occupation_ids, filters.industry_ids, filters.characteristic_ids, filters.prefecture_ids, filters.job_source_ids]);

  const fetchJobListings = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryString = buildQueryString(filters); // Already includes page parameter
      const response = await fetch(`/api/v1/job_listings/search${queryString}`, {
        headers: { 'Accept': 'application/json' }
      });
      const data = await response.json();
      setJobListings(data.job_listings);
      setPagination(data.pagination);

      // 検索イベントを送信（検索結果が取得できた後）
      if (data.pagination && data.pagination.total_count !== undefined) {
        trackJobSearch(filters, data.pagination.total_count);
      }
    } catch (error) {
      console.error('Failed to fetch job listings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // ブラウザの戻る/進むボタンに対応
  useEffect(() => {
    if (!isHydrated) return;

    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const filtersFromUrl = parseUrlParams(params);
      setFilters(filtersFromUrl);
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isHydrated]);

  // フィルタ変更時にAPIリクエスト＆URL更新（SSR初回はスキップ）
  useEffect(() => {
    if (!isHydrated) return; // SSR時はスキップ
    
    // 初回ハイドレーション時は既にデータがあるのでAPIコールをスキップ
    if (hasInitialData.current && isServerRendered) {
      hasInitialData.current = false; // フラグをリセット
      return;
    }
    
    // Local Storageに検索条件を保存
    saveSearchConditions({
      keyword: filters.keyword,
      skill_ids: filters.skill_ids,
      occupation_ids: filters.occupation_ids,
      industry_ids: filters.industry_ids,
      characteristic_ids: filters.characteristic_ids,
      prefecture_ids: filters.prefecture_ids,
      workstyle_types: filters.workstyle_types,
      availability_types: filters.availability_types,
      price_over: filters.price_over,
      is_recruiting: filters.is_recruiting,
      sort: filters.sort
    });
    
    fetchJobListings();
    
    // URLを更新（常にpushStateを使用して履歴を保持）
    updateUrl(false);
  }, [filters, isHydrated, fetchJobListings, isServerRendered]);

  const updateUrl = (replaceState = false) => {
    if (typeof window === 'undefined') return; // SSR時のガード
    
    const queryString = buildQueryString(filters);
    const newUrl = `${window.location.pathname}${queryString}`;
    
    // 現在のURLと異なる場合のみ履歴を更新
    if (window.location.href !== newUrl) {
      if (replaceState) {
        window.history.replaceState({}, '', newUrl);
      } else {
        window.history.pushState({}, '', newUrl);
      }
    }
  };

  const updateFilters = (newFilters: Partial<SearchFilters>) => {
    // pageが明示的に指定されている場合はそれを使用、そうでない場合は1にリセット
    const pageValue = newFilters.page !== undefined ? newFilters.page : 1;
    const isPageChange = newFilters.page !== undefined;
    
    const newState = { ...filters, ...newFilters, page: pageValue };
    
    setFilters(newState);
    
    // SSR時はスキップ
    if (typeof window === 'undefined') return;
    
    // 即座にURLを更新
    const queryString = buildQueryString(newState);
    const newUrl = `${window.location.pathname}${queryString}`;
    
    if (window.location.href !== newUrl) {
      window.history.pushState({}, '', newUrl);
    }
    
    // ページネーションクリック時のスクロール動作
    if (isPageChange) {
      setTimeout(() => {
        if (window.innerWidth <= 768) {
          // モバイル: ページトップにスクロール
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          // デスクトップ: 求人一覧コンテナの先頭にスクロール
          const targetElement = document.querySelector('[data-job-listings]');
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 300);
    }
  };

  // 検索条件をMobileSearchModalの形式に変換
  const convertFiltersToModalFormat = (): {
    keyword: string;
    prefecture_ids: number[];
    price_over?: number;
    is_recruiting: boolean;
    remote_types: string[];
    skill_ids: number[];
    occupation_ids: number[];
    industry_ids: number[];
    workday_types: string[];
    characteristic_ids: number[];
    job_source_ids?: number[];
  } => {
    return {
      keyword: filters.keyword || '',
      prefecture_ids: filters.prefecture_ids || [],
      price_over: filters.price_over,
      is_recruiting: filters.is_recruiting || false,
      remote_types: filters.remote_types || [],
      skill_ids: filters.skill_ids || [],
      occupation_ids: filters.occupation_ids || [],
      industry_ids: filters.industry_ids || [],
      workday_types: filters.workday_types || [],
      characteristic_ids: filters.characteristic_ids || [],
      job_source_ids: filters.job_source_ids || []
    };
  };

  // MobileSearchModalからの検索実行（/job_listings/searchにリダイレクト）
  const handleMobileSearch = (searchFilters: {
    keyword: string;
    prefecture_ids: number[];
    price_over?: number;
    is_recruiting: boolean;
    remote_types: string[];
    skill_ids: number[];
    occupation_ids: number[];
    industry_ids: number[];
    workday_types: string[];
    characteristic_ids: number[];
    job_source_ids?: number[];
  }) => {
    const params = new URLSearchParams();
    
    if (searchFilters.keyword) params.append('keyword', searchFilters.keyword);
    searchFilters.prefecture_ids.forEach(id => params.append('prefecture_ids[]', id.toString()));
    if (searchFilters.price_over) params.append('price_over', searchFilters.price_over.toString());
    if (searchFilters.is_recruiting) params.append('is_recruiting', 'true');
    searchFilters.remote_types.forEach(type => params.append('remote_types[]', type));
    searchFilters.skill_ids.forEach(id => params.append('skill_ids[]', id.toString()));
    searchFilters.occupation_ids.forEach(id => params.append('occupation_ids[]', id.toString()));
    searchFilters.industry_ids.forEach(id => params.append('industry_ids[]', id.toString()));
    searchFilters.workday_types.forEach(type => params.append('workday_types[]', type));
    searchFilters.characteristic_ids.forEach(id => params.append('characteristic_ids[]', id.toString()));
    if (searchFilters.job_source_ids && searchFilters.job_source_ids.length > 0) {
      searchFilters.job_source_ids.forEach(id => params.append('job_source_ids[]', id.toString()));
    }
    
    const queryString = params.toString();
    window.location.href = `/job_listings/search${queryString ? `?${queryString}` : ''}`;
  };

  const removeFilter = (filterKey: string, value?: any) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      
      // valueが指定されていない場合は、フィルター全体を削除
      if (value === undefined) {
        delete newFilters[filterKey as keyof SearchFilters];
      } else if (filterKey.endsWith('_ids')) {
        // ID配列のフィルター（skill_ids, occupation_ids, industry_ids, characteristic_ids, job_source_ids, prefecture_ids）
        const arrayKey = filterKey as keyof Pick<SearchFilters, 'skill_ids' | 'occupation_ids' | 'industry_ids' | 'characteristic_ids' | 'job_source_ids' | 'prefecture_ids'>;
        if (newFilters[arrayKey]) {
          // 型の不一致を防ぐため、両方を数値に変換して比較
          const valueNum = typeof value === 'string' ? parseInt(value, 10) : value;
          newFilters[arrayKey] = newFilters[arrayKey]!.filter(id => {
            const idNum = typeof id === 'string' ? parseInt(id, 10) : id;
            return idNum !== valueNum;
          });
          if (newFilters[arrayKey]!.length === 0) {
            delete newFilters[arrayKey];
          }
        }
      } else if (filterKey === 'workstyle_types' || filterKey === 'availability_types' || filterKey === 'remote_types' || filterKey === 'workday_types') {
        // 文字列配列のフィルター（workstyle_types, availability_types, remote_types, workday_types）
        const arrayKey = filterKey as keyof Pick<SearchFilters, 'workstyle_types' | 'availability_types' | 'remote_types' | 'workday_types'>;
        if (newFilters[arrayKey]) {
          // 文字列として比較（型変換を考慮）
          newFilters[arrayKey] = newFilters[arrayKey]!.filter(type => String(type) !== String(value));
          if (newFilters[arrayKey]!.length === 0) {
            delete newFilters[arrayKey];
          }
        }
      } else {
        delete newFilters[filterKey as keyof SearchFilters];
      }
      
      return { ...newFilters, page: 1 };
    });
  };

  const handleJobSelect = async (jobId: number) => {
    setSelectedJobId(jobId);
    setIsLoading(true);
    
    try {
      const response = await fetch(`/api/v1/job_listings/${jobId}`, {
        headers: { 'Accept': 'application/json' }
      });
      const jobDetail = await response.json();
      setSelectedJobDetail(jobDetail);
    } catch (error) {
      console.error('Failed to fetch job detail:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJobClose = () => {
    setSelectedJobId(null);
    setSelectedJobDetail(null);
  };

  // 検索条件を正規化して比較用のオブジェクトを作成
  const normalizeSearchParams = (params: SearchFilters): Record<string, any> => {
    const normalized: Record<string, any> = {};
    
    // 配列はソートして比較
    if (params.skill_ids && params.skill_ids.length > 0) {
      normalized.skill_ids = [...params.skill_ids].sort((a, b) => a - b);
    }
    if (params.occupation_ids && params.occupation_ids.length > 0) {
      normalized.occupation_ids = [...params.occupation_ids].sort((a, b) => a - b);
    }
    if (params.industry_ids && params.industry_ids.length > 0) {
      normalized.industry_ids = [...params.industry_ids].sort((a, b) => a - b);
    }
    if (params.characteristic_ids && params.characteristic_ids.length > 0) {
      normalized.characteristic_ids = [...params.characteristic_ids].sort((a, b) => a - b);
    }
    if (params.prefecture_ids && params.prefecture_ids.length > 0) {
      normalized.prefecture_ids = [...params.prefecture_ids].sort((a, b) => a - b);
    }
    if (params.workstyle_types && params.workstyle_types.length > 0) {
      normalized.workstyle_types = [...params.workstyle_types].sort();
    }
    if (params.availability_types && params.availability_types.length > 0) {
      normalized.availability_types = [...params.availability_types].sort();
    }
    if (params.remote_types && params.remote_types.length > 0) {
      normalized.remote_types = [...params.remote_types].sort();
    }
    if (params.workday_types && params.workday_types.length > 0) {
      normalized.workday_types = [...params.workday_types].sort();
    }
    if (params.job_source_ids && params.job_source_ids.length > 0) {
      normalized.job_source_ids = [...params.job_source_ids].sort((a, b) => a - b);
    }
    
    // その他のフィールド
    if (params.keyword) normalized.keyword = params.keyword.trim();
    if (params.price_over) normalized.price_over = params.price_over;
    if (params.payment_site) normalized.payment_site = params.payment_site;
    if (params.settlement_hours) normalized.settlement_hours = params.settlement_hours;
    if (params.interview_count_max) normalized.interview_count_max = params.interview_count_max;
    if (params.published_within) normalized.published_within = params.published_within;
    if (params.is_recruiting !== undefined) normalized.is_recruiting = params.is_recruiting;
    
    return normalized;
  };

  // 検索条件が一致するかチェック
  const areSearchParamsEqual = (params1: Record<string, any>, params2: Record<string, any>): boolean => {
    const keys1 = Object.keys(params1).sort();
    const keys2 = Object.keys(params2).sort();
    
    if (keys1.length !== keys2.length) return false;
    
    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      
      const val1 = params1[key];
      const val2 = params2[key];
      
      if (Array.isArray(val1) && Array.isArray(val2)) {
        if (val1.length !== val2.length) return false;
        for (let i = 0; i < val1.length; i++) {
          if (val1[i] !== val2[i]) return false;
        }
      } else if (val1 !== val2) {
        return false;
      }
    }
    
    return true;
  };

  // 現在の検索条件が保存済みかチェック
  useEffect(() => {
    const checkSavedCondition = async () => {
      setIsCheckingSavedCondition(true);
      try {
        const endpoint = isUserSignedIn
          ? '/saved_search_conditions'
          : '/guest_email_subscriptions/saved_search_conditions';

        const response = await fetch(endpoint, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success' && data.saved_search_conditions) {
            const currentParams = normalizeSearchParams(filters);
            
            // pageとsortを除いた検索条件で比較
            const { page, sort, ...searchParams } = currentParams;
            const normalizedCurrent = normalizeSearchParams(searchParams as SearchFilters);
            
            const matchingCondition = data.saved_search_conditions.find((condition: any) => {
              const savedParams = normalizeSearchParams(condition.search_params);
              return areSearchParamsEqual(normalizedCurrent, savedParams);
            });
            
            if (matchingCondition) {
              setSavedSearchConditionId(matchingCondition.id);
            } else {
              setSavedSearchConditionId(null);
            }
          }
        }
      } catch (error) {
        console.error('Error checking saved condition:', error);
      } finally {
        setIsCheckingSavedCondition(false);
      }
    };

    // 検索条件が変更されたらチェック（チェック中でない場合のみ）
    if (isHydrated && !isCheckingSavedCondition) {
      checkSavedCondition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, isUserSignedIn, isHydrated]);

  // 検索条件の保存/削除を切り替え
  const handleToggleSaveSearchCondition = async () => {
    // SSR時はスキップ
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    if (savedSearchConditionId) {
      // 削除
      try {
        const response = await fetch(`/saved_search_conditions/${savedSearchConditionId}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
          }
        });
        
        if (response.ok) {
          setSavedSearchConditionId(null);
          if (window.showGlobalFlash) {
            window.showGlobalFlash('検索条件の保存を解除しました', 'success', false);
          }
        } else {
          throw new Error('Failed to delete');
        }
      } catch (error) {
        console.error('Error deleting saved condition:', error);
        if (window.showGlobalFlash) {
          window.showGlobalFlash('検索条件の削除に失敗しました', 'error', false);
        }
      }
    } else {
      // 保存モーダルを表示
      setShowSaveSearchConditionModal(true);
    }
  };

  return (
    <div>
      <TopSearchBar 
        keyword={filters.keyword || ''}
        prefectureIds={filters.prefecture_ids || []}
        priceOver={filters.price_over}
        isRecruiting={filters.is_recruiting || false}
        remoteTypes={filters.remote_types || []}
        skillIds={filters.skill_ids || []}
        occupationIds={filters.occupation_ids || []}
        industryIds={filters.industry_ids || []}
        workdayTypes={filters.workday_types || []}
        characteristicIds={filters.characteristic_ids || []}
        jobSourceIds={filters.job_source_ids || []}
        skillNames={skillNames}
        occupationNames={occupationNames}
        industryNames={industryNames}
        characteristicNames={characteristicNames}
        prefectureNames={prefectureNames}
        jobSourceNames={jobSourceNames}
        onSearch={(searchFilters: SearchFilters) => updateFilters(searchFilters)}
      />
      
      <div className="container-fluid">
        <div className="row g-0 mt-0">
          {/* 求人一覧（左カラム） */}
          <div className="col-12 col-md-4 col-lg-4 d-flex flex-column" data-job-listings="true">
            <JobListingList
            jobListings={jobListings}
            selectedJobId={selectedJobId || undefined}
            onJobSelect={handleJobSelect}
            totalCount={pagination?.total_count || 0}
            from={pagination?.from || 0}
            to={pagination?.to || 0}
            currentPage={filters.page || 1}
            perPage={pagination?.per_page || 40}
            onPageChange={(page) => updateFilters({ page })}
            sortBy={filters.sort || 'created_at desc'}
            onSortChange={(sort) => updateFilters({ sort })}
            searchFilters={filters}
            onRemoveFilter={removeFilter}
            skillNames={skillNames}
            occupationNames={occupationNames}
            industryNames={industryNames}
            characteristicNames={characteristicNames}
            prefectureNames={prefectureNames}
            isLoading={isLoading}
            onSaveSearchCondition={handleToggleSaveSearchCondition}
            isSearchConditionSaved={savedSearchConditionId !== null}
          />
          </div>
          
          {/* 求人詳細（右カラム） */}
          <div className="col-md-8 col-lg-8 d-none d-md-block">
            <JobDetailPanel
              jobDetail={selectedJobDetail}
              onClose={handleJobClose}
              currentUserId={currentUserId}
              isUserSignedIn={isUserSignedIn}
              isProfileComplete={isProfileComplete}
              isLoading={isLoading}
              totalJobListingsCount={totalJobListingsCount}
            />
          </div>
        </div>
      </div>

      {/* SP用フィルタモーダル（クライアント側のみ） */}
      {isHydrated && (
        <MobileFilterModal 
          show={showMobileFilters}
          filters={filters}
          onFilterChange={updateFilters}
          onClose={() => setShowMobileFilters(false)}
        />
      )}

      {/* 検索条件保存モーダル */}
      {isHydrated && (
        <SaveSearchConditionModal
          isOpen={showSaveSearchConditionModal}
          onClose={() => setShowSaveSearchConditionModal(false)}
          searchParams={filters}
          isUserSignedIn={isUserSignedIn}
          onSuccess={() => {
            setShowSaveSearchConditionModal(false);
            // 保存後に再チェック（少し遅延させてサーバー側の処理が完了するまで待つ）
            setTimeout(() => {
              // 再チェックをトリガーするために、isCheckingSavedConditionをfalseにする
              setIsCheckingSavedCondition(false);
            }, 500);
          }}
        />
      )}

      {/* スマホ用検索条件モーダル */}
      {isHydrated && (
        <MobileSearchModal
          show={showMobileSearchModal}
          onClose={() => setShowMobileSearchModal(false)}
          onSearch={handleMobileSearch}
          initialFilters={convertFiltersToModalFormat()}
          skillNames={skillNames}
          occupationNames={occupationNames}
          industryNames={industryNames}
          characteristicNames={characteristicNames}
          prefectureNames={prefectureNames}
          jobSourceNames={jobSourceNames}
        />
      )}

    </div>
  );
};

// ヘルパー関数
const parseUrlParams = (params: URLSearchParams): SearchFilters => {
  return {
    keyword: params.get('keyword') || '',
    skill_ids: params.getAll('skill_ids[]').map(Number).filter(Boolean),
    occupation_ids: params.getAll('occupation_ids[]').map(Number).filter(Boolean),
    industry_ids: params.getAll('industry_ids[]').map(Number).filter(Boolean),
    characteristic_ids: params.getAll('characteristic_ids[]').map(Number).filter(Boolean),
    prefecture_ids: params.getAll('prefecture_ids[]').map(Number).filter(Boolean),
    workstyle_types: params.getAll('workstyle_types[]'),
    availability_types: params.getAll('availability_types[]'),
    price_over: params.get('price_over') ? Number(params.get('price_over')) : undefined,
    payment_site: params.get('payment_site') || '',
    settlement_hours: params.get('settlement_hours') || '',
    interview_count_max: params.get('interview_count_max') ? Number(params.get('interview_count_max')) : undefined,
    published_within: params.get('published_within') ? Number(params.get('published_within')) : undefined,
    is_recruiting: params.get('is_recruiting') === 'true',
    remote_types: params.getAll('remote_types[]'),
    workday_types: params.getAll('workday_types[]'),
    job_source_ids: params.getAll('job_source_ids[]').map(Number).filter(Boolean),
    page: parseInt(params.get('page') || '1'),
    sort: params.get('sort') || 'created_at desc'
  };
};

const buildQueryString = (filters: SearchFilters): string => {
  const params = new URLSearchParams();
  
  if (filters.keyword) params.append('keyword', filters.keyword);
  filters.skill_ids?.forEach(id => params.append('skill_ids[]', id.toString()));
  filters.occupation_ids?.forEach(id => params.append('occupation_ids[]', id.toString()));
  filters.industry_ids?.forEach(id => params.append('industry_ids[]', id.toString()));
  filters.characteristic_ids?.forEach(id => params.append('characteristic_ids[]', id.toString()));
  filters.prefecture_ids?.forEach(id => params.append('prefecture_ids[]', id.toString()));
  filters.workstyle_types?.forEach(type => params.append('workstyle_types[]', type));
  filters.availability_types?.forEach(type => params.append('availability_types[]', type));
  if (filters.price_over) params.append('price_over', filters.price_over.toString());
  if (filters.payment_site) params.append('payment_site', filters.payment_site);
  if (filters.settlement_hours) params.append('settlement_hours', filters.settlement_hours);
  if (filters.interview_count_max) params.append('interview_count_max', filters.interview_count_max.toString());
  if (filters.published_within) params.append('published_within', filters.published_within.toString());
  if (filters.is_recruiting) params.append('is_recruiting', 'true');
  filters.remote_types?.forEach(type => params.append('remote_types[]', type));
  filters.workday_types?.forEach(type => params.append('workday_types[]', type));
  filters.job_source_ids?.forEach(id => params.append('job_source_ids[]', id.toString()));
  if (filters.sort) params.append('sort', filters.sort);
  if (filters.page && filters.page > 1) params.append('page', filters.page.toString());
  
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

export default JobSearchPage;
